import { useCallback } from 'react';
import { toast } from '@/hooks/use-toast';
import { useWebSocketChannel } from '@/hooks/useWebSocketChannel';

type BlockedCustomerAlert = {
  customerId: string;
  customerName?: string;
  reason?: string;
  source: 'CHAT' | 'COMMENT' | 'CART';
  text?: string;
};

const SOURCE_LABEL: Record<string, string> = {
  CHAT: 'by chat',
  CART: 'by adding to cart',
  COMMENT: 'by comment',
};

/**
 * App-wide listener: tells the tenant on any page when a blocked customer tries to order.
 * Comment attempts are already toasted by the live comment panel, so they are skipped here.
 */
export default function BlockedCustomerAlertListener() {
  const handle = useCallback((stompMsg: { body: string }) => {
    try {
      const event = JSON.parse(stompMsg.body);
      if (event.type !== 'BLOCKED_CUSTOMER_ATTEMPT') return;
      const alert = event.payload as BlockedCustomerAlert;
      if (alert.source === 'COMMENT') return;
      toast({
        title: 'Blocked customer tried to order',
        description: `${alert.customerName ?? 'Customer'} ${SOURCE_LABEL[alert.source] ?? ''}${
          alert.reason ? ` — ${alert.reason}` : ''
        }`,
        variant: 'destructive',
      });
    } catch (e) {
      console.warn('Blocked customer WS parse error', e);
    }
  }, []);

  useWebSocketChannel('/topic/blocked-customers', handle);
  return null;
}
